/**
 * Theme color constants
 * Centralized color definitions for consistent theming across the application
 */

// Light mode colors
export const lightColors = {
  background: {
    default: '#F8FAFC',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#0F172A',
    secondary: '#475569',
  },
  divider: '#E2E8F0',
} as const;

// Dark mode colors (Slate palette from Tailwind)
export const darkColors = {
  background: {
    default: '#0F172A', // slate-900
    paper: '#1E293B',   // slate-800
  },
  text: {
    primary: '#F1F5F9',  // slate-100
    secondary: '#94A3B8', // slate-400
  },
  divider: '#334155', // slate-700
  border: '#475569',  // slate-600
  borderHover: '#64748B', // slate-500
  action: {
    active: '#F1F5F9',
    hover: 'rgba(248, 250, 252, 0.08)',
    selected: 'rgba(248, 250, 252, 0.16)',
    disabled: 'rgba(248, 250, 252, 0.3)',
    disabledBackground: 'rgba(248, 250, 252, 0.12)',
  },
} as const;

// Semantic colors for light mode
export const lightSemanticColors = {
  success: { main: '#059669', light: '#10B981', dark: '#047857', contrastText: '#FFFFFF' },
  warning: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706', contrastText: '#78350F' },
  error: { main: '#DC2626', light: '#EF4444', dark: '#B91C1C', contrastText: '#FFFFFF' },
  info: { main: '#0284C7', light: '#0EA5E9', dark: '#0369A1', contrastText: '#FFFFFF' },
} as const;

// Semantic colors for dark mode (lighter variants for better visibility)
export const darkSemanticColors = {
  success: { main: '#34D399', light: '#6EE7B7', dark: '#10B981', contrastText: '#0F172A' },
  warning: { main: '#FBBF24', light: '#FCD34D', dark: '#F59E0B', contrastText: '#78350F' },
  error: { main: '#F87171', light: '#FCA5A5', dark: '#EF4444', contrastText: '#0F172A' },
  info: { main: '#38BDF8', light: '#7DD3FC', dark: '#0EA5E9', contrastText: '#0F172A' },
} as const;

// Default application colors
export const defaultColors = {
  primary: {
    main: '#0F6466',  // Deep Teal
    light: '#14919B',
    dark: '#0A4A4C',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#3681c3',  // Modern blue
    light: '#5c9dd6',
    dark: '#2563a0',
    contrastText: '#FFFFFF',
  },
} as const;

// Admin dark mode primary (lighter teal for visibility)
export const adminDarkColors = {
  primary: {
    main: '#14B8A6',
    light: '#2DD4BF',
    dark: '#0D9488',
    contrastText: '#0F172A',
  },
  secondary: {
    main: '#60A5FA',
    light: '#93C5FD',
    dark: '#3B82F6',
    contrastText: '#0F172A',
  },
} as const;

// Sysadmin colors (Neosapiens blue theme - professional and authoritative)
export const sysadminColors = {
  light: {
    primary: { main: '#1E3A5F', light: '#2D5A8C', dark: '#152A45', contrastText: '#FFFFFF' },  // Navy blue
    secondary: { main: '#3B82F6', light: '#60A5FA', dark: '#2563EB', contrastText: '#FFFFFF' }, // Accent blue
  },
  dark: {
    primary: { main: '#60A5FA', light: '#93C5FD', dark: '#3B82F6', contrastText: '#0F172A' },   // Lighter blue
    secondary: { main: '#38BDF8', light: '#7DD3FC', dark: '#0EA5E9', contrastText: '#0F172A' }, // Sky blue accent
  },
} as const;
